// Types for the AI course-generation feature. Fields are snake_case to mirror
// the Marshmallow output from the backend.

export type AIJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface AICourseJob {
  id: string;
  tenant_id: string;
  course_id: string | null;
  status: AIJobStatus;
  progress_percent: number;
  current_step: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AIGenerateClarification {
  question: string;
  answer: string;
}

/** Where AI-generated imagery may be used in the course. */
export type AIImagesOption = 'none' | 'headers' | 'content' | 'both';

/** Target course length; 'auto' lets the AI size it from the content. */
export type AIDurationOption = 'auto' | 'quick' | 'standard' | 'deep';

/** How learners browse the finished course's sections. */
export type AILayoutOption = 'cards' | 'list';

export interface AIGenerateRequest {
  topic: string;
  urls?: string[];
  file_keys?: string[];
  clarifications?: AIGenerateClarification[];
  include_quizzes?: boolean;
  preferences?: string;
  include_survey?: boolean;
  include_certificate?: boolean;
  images?: AIImagesOption;
  duration?: AIDurationOption;
  layout?: AILayoutOption;
  idempotency_key?: string;
}

export interface AIGenerateResult {
  job_id: string;
  course_id: string | null;
  status: AIJobStatus;
}

// Per-tenant AI opt-in state (drives whether the UI surfaces AI entry points).
export interface AISettings {
  available: boolean; // system master switch
  enabled: boolean; // tenant opt-in
  consent_at: string | null;
  consented_by: string | null;
}

export const AI_TERMINAL_STATUSES: AIJobStatus[] = ['succeeded', 'failed', 'cancelled'];

export const isAITerminal = (status: AIJobStatus): boolean =>
  AI_TERMINAL_STATUSES.includes(status);
